import React, { useState, useEffect, useRef } from 'react';
import { 
  User, Palette, Building2, Truck, Bell, Map, Settings, Key, 
  Shield, Save, Upload, Check, ChevronRight, Globe, Phone, RefreshCw,
  Users, Search, Edit2, Lock, Unlock, Trash2, X, Eye, EyeOff, UserPlus,
  MapPin, Banknote, Clock, Image, Coins, Plus, Mail, Send, FileText, CreditCard
} from 'lucide-react';
import api from '../../services/api';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Helper to get full image URL
const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${API_BASE}${path}`;
};

// Email Reports Section Component
const EmailReportsSection = () => {
  const [smtpConfig, setSmtpConfig] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: 'TIATaxi',
    smtp_use_tls: true,
    report_enabled: false,
    report_day: 1,
    admin_email: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    fetchSmtpSettings();
  }, []);

  const fetchSmtpSettings = async () => {
    try {
      const response = await api.get('/settings/smtp');
      setSmtpConfig(prev => ({ ...prev, ...response.data }));
    } catch (err) {
      console.error('Error fetching SMTP settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/settings/smtp', smtpConfig);
      toast.success('Konfigurimi SMTP u ruajt me sukses!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gabim gjatë ruajtjes');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!smtpConfig.admin_email) {
      toast.error('Ju lutem vendosni email-in e adminit');
      return;
    }
    setTesting(true);
    try {
      const response = await api.post('/settings/smtp/test', { test_email: smtpConfig.admin_email });
      toast.success(response.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gabim gjatë testimit');
    } finally {
      setTesting(false);
    }
  };

  const handleSendReport = async () => {
    setSendingReport(true);
    try {
      const response = await api.post('/reports/monthly-expenses/send');
      toast.success(response.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gabim gjatë dërgimit të raportit');
    } finally {
      setSendingReport(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-gray-400" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Konfigurim SMTP</h3>
          <p className="text-sm text-gray-500">Konfiguro SMTP dhe raportet automatike mujore</p>
        </div>
      </div>

      {/* SMTP Configuration */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0">
            <Mail className="w-6 h-6 text-white" />
          </div>
          <div>
            <h4 className="text-base font-semibold text-gray-900 mb-1">Konfigurimi SMTP</h4>
            <p className="text-sm text-gray-500">Vendosni të dhënat e serverit SMTP për dërgimin e email-eve</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Server SMTP</label>
            <input
              type="text"
              value={smtpConfig.smtp_host}
              onChange={(e) => setSmtpConfig({...smtpConfig, smtp_host: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="smtp.gmail.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Porti</label>
            <input
              type="number"
              value={smtpConfig.smtp_port}
              onChange={(e) => {
                const port = parseInt(e.target.value, 10) || 587;
                setSmtpConfig({
                  ...smtpConfig,
                  smtp_port: port,
                  // Port 465 uses implicit SSL, not STARTTLS
                  smtp_use_tls: port === 465 ? false : smtpConfig.smtp_use_tls,
                });
              }}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="587"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
            <input
              type="text"
              value={smtpConfig.smtp_user}
              onChange={(e) => setSmtpConfig({...smtpConfig, smtp_user: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="user@gmail.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={smtpConfig.smtp_password}
                onChange={(e) => setSmtpConfig({...smtpConfig, smtp_password: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg pr-10"
                placeholder={smtpConfig.has_password ? "••••••••" : "Password"}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email Dërguesi</label>
            <input
              type="email"
              value={smtpConfig.smtp_from_email}
              onChange={(e) => setSmtpConfig({...smtpConfig, smtp_from_email: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="noreply@tiataxi.al"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Emri Dërguesit</label>
            <input
              type="text"
              value={smtpConfig.smtp_from_name}
              onChange={(e) => setSmtpConfig({...smtpConfig, smtp_from_name: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="TIATaxi"
            />
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2">
          <input
            type="checkbox"
            id="use_tls"
            checked={smtpConfig.smtp_use_tls}
            disabled={smtpConfig.smtp_port === 465}
            onChange={(e) => setSmtpConfig({...smtpConfig, smtp_use_tls: e.target.checked})}
            className="rounded"
          />
          <label htmlFor="use_tls" className="text-sm text-gray-700">Përdor TLS/STARTTLS</label>
        </div>
        {smtpConfig.smtp_port === 465 && (
          <p className="text-xs text-amber-700 mt-2">
            Porti 465 përdor SSL të drejtpërdrejtë. Lëreni STARTTLS të çaktivizuar (si tani).
          </p>
        )}
      </div>

      {/* Report Configuration */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center flex-shrink-0">
            <FileText className="w-6 h-6 text-white" />
          </div>
          <div>
            <h4 className="text-base font-semibold text-gray-900 mb-1">Raporti Mujor i Shpenzimeve</h4>
            <p className="text-sm text-gray-500">Konfiguro raportin automatik që dërgohet çdo muaj</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email i Adminit (Marrësi)</label>
            <input
              type="email"
              value={smtpConfig.admin_email}
              onChange={(e) => setSmtpConfig({...smtpConfig, admin_email: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="admin@tiataxi.al"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Dita e Dërgimit</label>
            <select
              value={smtpConfig.report_day}
              onChange={(e) => setSmtpConfig({...smtpConfig, report_day: parseInt(e.target.value)})}
              className="w-full px-3 py-2 border rounded-lg"
            >
              {[1, 2, 3, 4, 5, 10, 15, 20, 25].map(d => (
                <option key={d} value={d}>Dita {d} e muajit</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2">
          <input
            type="checkbox"
            id="report_enabled"
            checked={smtpConfig.report_enabled}
            onChange={(e) => setSmtpConfig({...smtpConfig, report_enabled: e.target.checked})}
            className="rounded"
          />
          <label htmlFor="report_enabled" className="text-sm text-gray-700">Aktivizo dërgimin automatik të raportit mujor</label>
        </div>

        <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-700">
            <strong>Info:</strong> Raporti përfshin tabelë HTML me përmbledhjen e shpenzimeve dhe PDF të bashkangjitur me detajet e plota sipas automjetit.
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
        >
          {saving ? <RefreshCw size={16} className="animate-spin" /> : <Save size={16} />}
          Ruaj Konfigurimin
        </button>
        <button
          onClick={handleTestEmail}
          disabled={testing || !smtpConfig.smtp_host}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          {testing ? <RefreshCw size={16} className="animate-spin" /> : <Send size={16} />}
          Testo Email-in
        </button>
        <button
          onClick={handleSendReport}
          disabled={sendingReport || !smtpConfig.smtp_host || !smtpConfig.admin_email}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
        >
          {sendingReport ? <RefreshCw size={16} className="animate-spin" /> : <FileText size={16} />}
          Dërgo Raportin Tani
        </button>
      </div>
    </div>
  );
};

const SettingsPage = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // File upload refs
  const logoInputRef = useRef(null);
  const smallLogoInputRef = useRef(null);
  const faviconInputRef = useRef(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingSmallLogo, setUploadingSmallLogo] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  
  // Exchange rate state
  const [exchangeRate, setExchangeRate] = useState({
    eur_to_lek: 100,
    updated_at: null,
    updated_by_name: null
  });
  const [newRate, setNewRate] = useState('');
  const [exchangeHistory, setExchangeHistory] = useState([]);
  const [loadingExchange, setLoadingExchange] = useState(false);
  
  // All settings state
  const [settings, setSettings] = useState({
    // General
    admin_first_name: 'Admin',
    admin_last_name: '',
    admin_email: 'admin@tiataxi.al',
    admin_phone: '+355 69 000 0000',
    admin_username: 'admin',
    
    // Appearance
    theme: 'light',
    primary_color: '#dc2313',
    accent_color: '#fabc0c',
    language: 'sq',
    date_format: 'DD/MM/YYYY',
    time_format: '24h',
    timezone: 'Europe/Tirane',
    base_currency: 'LEK',
    
    // Branding
    company_name: 'TIATaxi Albania',
    tagline: 'Shërbimi më i besueshëm i taksive',
    logo_url: '',
    small_logo_url: '',
    favicon_url: '',
    support_email: 'support@tiataxi.al',
    support_phone: '+355 69 000 0000',
    
    // Dispatch
    auto_assign: true,
    max_wait_time: 15,
    search_radius: 5,
    prioritize_rating: true,
    allow_manual_override: true,
    notify_driver_sms: true,
    
    // Notifications
    email_notifications: true,
    sms_notifications: true,
    push_notifications: true,
    new_ride_alert: true,
    driver_offline_alert: true,
    low_balance_alert: true,
    daily_report: true,
    weekly_report: false,
    
    // Map
    default_lat: 41.3275,
    default_lng: 19.8187,
    default_zoom: 13,
    map_style: 'standard',
    show_traffic: true,
    show_drivers: true,
    auto_refresh: true,
    refresh_interval: 10,
    
    // System
    currency: 'LEK',
    secondary_currency: 'LEK',
    exchange_rate: 100,
    maintenance_mode: false,
    debug_mode: false,
    log_level: 'info'
  });

  // Password state (separate, not saved to DB)
  const [passwordSettings, setPasswordSettings] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Taximeter settings
  const [taximeterSettings, setTaximeterSettings] = useState({
    base_fare: 300,
    per_km: 100,
    per_minute: 30,
    waiting_per_minute: 50,
    night_surcharge: 0.20,
    weekend_surcharge: 0.10,
    night_start_hour: 22,
    night_end_hour: 6,
    min_fare: 300,
    currency: 'LEK',
    eur_rate: 100
  });

  // Users management state
  const [allUsers, setAllUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('all');
  const [editingUser, setEditingUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    username: '',
    email: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Add new user state
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [addUserForm, setAddUserForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    role: 'dispatcher'
  });
  const [showAddPassword, setShowAddPassword] = useState(false);
  const [addingUser, setAddingUser] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchTaximeterSettings();
  }, []);

  useEffect(() => {
    if (activeTab === 'exchange') {
      fetchExchangeRate();
      fetchExchangeHistory();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'users') {
      fetchAllUsers();
    }
  }, [activeTab]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/settings');
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Error fetching settings:', error);
      // Use defaults if fetch fails
    } finally {
      setLoading(false);
    }
  };

  // Exchange rate functions
  const fetchExchangeRate = async () => {
    try {
      setLoadingExchange(true);
      const response = await api.get('/settings/exchange-rate');
      setExchangeRate(response.data);
      setNewRate(response.data.eur_to_lek?.toString() || '100');
    } catch (error) {
      console.error('Error fetching exchange rate:', error);
    } finally {
      setLoadingExchange(false);
    }
  };

  const fetchExchangeHistory = async () => {
    try {
      const response = await api.get('/settings/exchange-rate/history');
      setExchangeHistory(response.data);
    } catch (error) {
      console.error('Error fetching exchange history:', error);
    }
  };

  const updateExchangeRate = async () => {
    const rateValue = parseFloat(newRate);
    if (!rateValue || rateValue <= 0) {
      toast.error('Kursi duhet të jetë numër pozitiv');
      return;
    }
    
    setSaving(true);
    try {
      const response = await api.put('/settings/exchange-rate', { new_rate: rateValue });
      toast.success(response.data.message);
      fetchExchangeRate();
      fetchExchangeHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim gjatë përditësimit të kursit');
    } finally {
      setSaving(false);
    }
  };

  const fetchTaximeterSettings = async () => {
    try {
      const response = await api.get('/taximeter/rates');
      setTaximeterSettings({
        base_fare: response.data.base_fare || 300,
        per_km: response.data.per_km || 100,
        per_minute: response.data.per_minute || 30,
        waiting_per_minute: response.data.waiting_per_minute || 50,
        night_surcharge: (response.data.night_surcharge_pct || 20) / 100,
        weekend_surcharge: (response.data.weekend_surcharge_pct || 10) / 100,
        night_start_hour: response.data.night_start_hour || 22,
        night_end_hour: response.data.night_end_hour || 6,
        min_fare: response.data.min_fare || 300,
        currency: response.data.currency || 'LEK',
        eur_rate: response.data.eur_rate || 100
      });
    } catch (error) {
      console.error('Error fetching taximeter settings:', error);
    }
  };

  const saveTaximeterSettings = async () => {
    setSaving(true);
    try {
      await api.put('/taximeter/rates', {
        taximeter_base_fare: taximeterSettings.base_fare,
        taximeter_per_km: taximeterSettings.per_km,
        taximeter_per_minute: taximeterSettings.per_minute,
        taximeter_waiting_per_minute: taximeterSettings.waiting_per_minute,
        taximeter_night_surcharge: taximeterSettings.night_surcharge,
        taximeter_weekend_surcharge: taximeterSettings.weekend_surcharge,
        taximeter_night_start_hour: taximeterSettings.night_start_hour,
        taximeter_night_end_hour: taximeterSettings.night_end_hour,
        taximeter_min_fare: taximeterSettings.min_fare,
        taximeter_eur_rate: taximeterSettings.eur_rate
      });
      toast.success('Cilësimet e taksimetrit u ruajtën me sukses!');
    } catch (error) {
      console.error('Error saving taximeter settings:', error);
      toast.error('Gabim gjatë ruajtjes së cilësimeve të taksimetrit');
    }
    setSaving(false);
  };

  const updateTaximeterSetting = (key, value) => {
    setTaximeterSettings(prev => ({ ...prev, [key]: value }));
  };

  // Users Management Functions
  const fetchAllUsers = async () => {
    setUsersLoading(true);
    try {
      const response = await api.get('/admin/users');
      setAllUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Gabim gjatë marrjes së përdoruesve');
    } finally {
      setUsersLoading(false);
    }
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    // Split the name into first and last name
    const nameParts = (user.name || '').split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';
    
    setEditForm({
      first_name: firstName,
      last_name: lastName,
      username: user.username || user.email?.split('@')[0] || '',
      email: user.email || '',
      newPassword: '',
      confirmPassword: ''
    });
    setShowNewPassword(false);
    setShowEditModal(true);
  };

  const handleUpdateUser = async () => {
    if (editForm.newPassword && editForm.newPassword !== editForm.confirmPassword) {
      toast.error('Fjalëkalimet nuk përputhen!');
      return;
    }
    if (editForm.newPassword && editForm.newPassword.length < 6) {
      toast.error('Fjalëkalimi duhet të ketë të paktën 6 karaktere');
      return;
    }

    setSaving(true);
    try {
      // Combine first and last name
      const fullName = `${editForm.first_name} ${editForm.last_name}`.trim();
      
      const updateData = {
        name: fullName,
        username: editForm.username,
        email: editForm.email
      };
      if (editForm.newPassword) {
        updateData.new_password = editForm.newPassword;
      }

      await api.put(`/admin/users/${editingUser.id}`, updateData);
      toast.success('Përdoruesi u përditësua me sukses!');
      setShowEditModal(false);
      fetchAllUsers();
      
      // Dispatch event to update header if current user was updated
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      if (currentUser.user_id === editingUser.id || currentUser.email === editingUser.email) {
        window.dispatchEvent(new CustomEvent('userUpdated'));
      }
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(error.response?.data?.detail || 'Gabim gjatë përditësimit');
    } finally {
      setSaving(false);
    }
  };

  const toggleUserStatus = async (user) => {
    try {
      const newStatus = user.login_enabled !== false ? false : true;
      await api.put(`/admin/users/${user.id}/toggle-login`, { enabled: newStatus });
      toast.success(newStatus ? 'Hyrja u aktivizua' : 'Hyrja u çaktivizua');
      fetchAllUsers();
    } catch (error) {
      console.error('Error toggling user status:', error);
      toast.error('Gabim gjatë ndryshimit të statusit');
    }
  };

  const openDeleteConfirm = (user) => {
    // Prevent deletion of admin@tiataxi.al
    if (user.email === 'admin@tiataxi.al') {
      toast.error('Administratori kryesor nuk mund të fshihet!');
      return;
    }
    setUserToDelete(user);
    setShowDeleteConfirm(true);
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    setDeleting(true);
    try {
      await api.delete(`/admin/users/${userToDelete.id}`);
      toast.success('Përdoruesi u fshi me sukses!');
      setShowDeleteConfirm(false);
      setUserToDelete(null);
      fetchAllUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Gabim gjatë fshirjes së përdoruesit');
    } finally {
      setDeleting(false);
    }
  };

  const openAddUserModal = () => {
    setAddUserForm({
      first_name: '',
      last_name: '',
      email: '',
      username: '',
      password: '',
      confirmPassword: '',
      role: 'dispatcher'
    });
    setShowAddPassword(false);
    setShowAddUserModal(true);
  };

  const handleAddUser = async () => {
    // Validation
    if (!addUserForm.first_name.trim()) {
      toast.error('Emri është i detyrueshëm');
      return;
    }
    if (!addUserForm.email.trim()) {
      toast.error('Email është i detyrueshëm');
      return;
    }
    if (!addUserForm.password) {
      toast.error('Fjalëkalimi është i detyrueshëm');
      return;
    }
    if (addUserForm.password.length < 6) {
      toast.error('Fjalëkalimi duhet të ketë të paktën 6 karaktere');
      return;
    }
    if (addUserForm.password !== addUserForm.confirmPassword) {
      toast.error('Fjalëkalimet nuk përputhen');
      return;
    }

    setAddingUser(true);
    try {
      const fullName = `${addUserForm.first_name} ${addUserForm.last_name}`.trim();
      await api.post('/admin/users', {
        name: fullName,
        email: addUserForm.email,
        username: addUserForm.username || addUserForm.email.split('@')[0],
        password: addUserForm.password,
        role: addUserForm.role
      });
      toast.success('Përdoruesi u krijua me sukses!');
      setShowAddUserModal(false);
      fetchAllUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error(error.response?.data?.detail || 'Gabim gjatë krijimit të përdoruesit');
    } finally {
      setAddingUser(false);
    }
  };

  const filteredUsers = allUsers.filter(user => {
    const matchesSearch = 
      (user.name?.toLowerCase().includes(userSearchTerm.toLowerCase())) ||
      (user.email?.toLowerCase().includes(userSearchTerm.toLowerCase())) ||
      (user.username?.toLowerCase().includes(userSearchTerm.toLowerCase()));
    
    const matchesRole = userRoleFilter === 'all' || user.role === userRoleFilter;
    
    return matchesSearch && matchesRole;
  });

  const getRoleBadge = (role) => {
    const badges = {
      admin: { bg: 'bg-red-100', text: 'text-red-700', label: 'Administrator' },
      dispatcher: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Dispatcher' },
      driver: { bg: 'bg-green-100', text: 'text-green-700', label: 'Shofer' },
    };
    const badge = badges[role] || { bg: 'bg-gray-100', text: 'text-gray-700', label: role };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  const getDefaultExpenseCategories = () => [
    { id: 'servis', name: 'Servis', active: true },
    { id: 'siguracion', name: 'Siguracion', active: true },
    { id: 'karburant', name: 'Karburant', active: true },
    { id: 'energji', name: 'Energji (Elektrike)', active: true },
    { id: 'kolaudim', name: 'Kolaudim', active: true },
    { id: 'taksa', name: 'Taksa', active: true },
    { id: 'other', name: 'Shpenzime të tjera', active: true }
  ];

  const getDefaultPaymentMethods = () => [
    { id: 'cash', name: 'Cash', active: true, for_dispatcher: true, for_driver: true },
    { id: 'pos', name: 'POS Bankar', active: true, for_dispatcher: true, for_driver: false }
  ];

  const menuItems = [
    { id: 'general', label: 'Konfigurime', icon: User },
    { id: 'users', label: 'Menaxhimi i Përdoruesve', icon: Users },
    { id: 'exchange', label: 'Kursi i Këmbimit', icon: Globe },
    { id: 'dispatch', label: 'Cilësimet e Dispatch', icon: Truck },
    { id: 'taximeter', label: 'Taksimetri Dixhital', icon: Settings, isConfig: true },
    { id: 'expense_categories', label: 'Kategori Shpenzimesh', icon: Banknote },
    { id: 'payment_methods', label: 'Metodat e Pagesës', icon: CreditCard },
    { id: 'smtp_config', label: 'Konfigurim SMTP', icon: Mail },
    { id: 'notifications', label: 'Njoftimet', icon: Bell },
    { id: 'api', label: 'Integrime API', icon: Key }
  ];

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/settings/section/${activeTab}`, settings);
      toast.success('Cilësimet u ruajtën me sukses!');
      
      // Notify other components that settings were updated
      window.dispatchEvent(new CustomEvent('settingsUpdated'));
      
      // Apply theme immediately if appearance was changed
      if (activeTab === 'appearance') {
        applyTheme(settings.theme, settings.primary_color);
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Gabim gjatë ruajtjes së cilësimeve');
    }
    setSaving(false);
  };

  const handlePasswordChange = async () => {
    if (passwordSettings.newPassword !== passwordSettings.confirmPassword) {
      toast.error('Fjalëkalimet nuk përputhen!');
      return;
    }
    if (passwordSettings.newPassword.length < 8) {
      toast.error('Fjalëkalimi duhet të ketë të paktën 8 karaktere');
      return;
    }
    
    setSaving(true);
    try {
      await api.put('/auth/change-password', {
        current_password: passwordSettings.currentPassword,
        new_password: passwordSettings.newPassword
      });
      toast.success('Fjalëkalimi u ndryshua me sukses!');
      setPasswordSettings({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim gjatë ndryshimit të fjalëkalimit');
    }
    setSaving(false);
  };

  const applyTheme = (theme, primaryColor) => {
    // Apply theme to document
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    // Apply primary color to CSS variable
    document.documentElement.style.setProperty('--primary-color', primaryColor);
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  // File upload handler
  const handleFileUpload = async (file, type) => {
    if (!file) return;
    
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast.error('Skedari është shumë i madh. Maksimumi është 5MB.');
      return;
    }

    const validTypes = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/x-icon', 'image/vnd.microsoft.icon'];
    if (!validTypes.includes(file.type)) {
      toast.error('Formati i skedarit nuk mbështetet. Përdorni PNG, JPG, SVG ose ICO.');
      return;
    }

    const setUploading = type === 'logo' ? setUploadingLogo : 
                         type === 'small_logo' ? setUploadingSmallLogo : setUploadingFavicon;
    const urlKey = type === 'logo' ? 'logo_url' : 
                   type === 'small_logo' ? 'small_logo_url' : 'favicon_url';

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', type);
      
      const response = await api.post('/settings/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.url) {
        updateSetting(urlKey, response.data.url);
        toast.success('Imazhi u ngarkua me sukses!');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Gabim gjatë ngarkimit të imazhit');
    }
    setUploading(false);
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
          <span className="ml-2 text-gray-500">Duke ngarkuar cilësimet...</span>
        </div>
      );
    }

    switch (activeTab) {
      case 'general':
        return (
          <div className="space-y-6">
            {/* Two column layout for all settings */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-6">
                {/* Company Information */}
                <div className="bg-white border border-gray-200 rounded-xl p-5">
                  <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-blue-600" />
                    Të Dhënat e Kompanisë
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Emri i Kompanisë</label>
                      <input
                        type="text"
                        value={settings.company_name || ''}
                        onChange={(e) => updateSetting('company_name', e.target.value)}
                        placeholder="TIATaxi Sh.p.k"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Emri Tregtar</label>
                      <input
                        type="text"
                        value={settings.trade_name || ''}
                        onChange={(e) => updateSetting('trade_name', e.target.value)}
                        placeholder="TIA Taxi"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">NIPT</label>
                      <input
                        type="text"
                        value={settings.nipt || ''}
                        onChange={(e) => updateSetting('nipt', e.target.value)}
                        placeholder="L12345678A"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm"
                      />
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="bg-white border border-gray-200 rounded-xl p-5">
                  <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Phone className="w-4 h-4 text-green-600" />
                    Kontaktet
                  </h3>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Telefon Kryesor</label>
                        <input
                          type="tel"
                          value={settings.company_phone || ''}
                          onChange={(e) => updateSetting('company_phone', e.target.value)}
                          placeholder="+355 69 123 4567"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Telefon Rezervime</label>
                        <input
                          type="tel"
                          value={settings.booking_phone || ''}
                          onChange={(e) => updateSetting('booking_phone', e.target.value)}
                          placeholder="+355 69 111 2222"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                      <input
                        type="email"
                        value={settings.company_email || ''}
                        onChange={(e) => updateSetting('company_email', e.target.value)}
                        placeholder="info@tiataxi.al"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Website</label>
                      <input
                        type="url"
                        value={settings.company_website || ''}
                        onChange={(e) => updateSetting('company_website', e.target.value)}
                        placeholder="https://tiataxi.al"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm"
                      />
                    </div>
                  </div>
                </div>

                {/* Address */}
                <div className="bg-white border border-gray-200 rounded-xl p-5">
                  <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-red-600" />
                    Adresa
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Adresa</label>
                      <input
                        type="text"
                        value={settings.company_address || ''}
                        onChange={(e) => updateSetting('company_address', e.target.value)}
                        placeholder="Rruga e Durrësit, Nr. 123"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Qyteti</label>
                        <input
                          type="text"
                          value={settings.company_city || 'Tiranë'}
                          onChange={(e) => updateSetting('company_city', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Kodi</label>
                        <input
                          type="text"
                          value={settings.company_postal_code || ''}
                          onChange={(e) => updateSetting('company_postal_code', e.target.value)}
                          placeholder="1001"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Shteti</label>
                        <input
                          type="text"
                          value={settings.company_country || 'Shqipëri'}
                          onChange={(e) => updateSetting('company_country', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-6">
                {/* Regional Settings */}
                <div className="bg-white border border-gray-200 rounded-xl p-5">
                  <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Globe className="w-4 h-4 text-purple-600" />
                    Cilësimet Rajonale
                  </h3>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Gjuha</label>
                        <select
                          value={settings.language}
                          onChange={(e) => updateSetting('language', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm"
                        >
                          <option value="sq">Shqip</option>
                          <option value="en">English</option>
                          <option value="it">Italiano</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Zona Kohore</label>
                        <select
                          value={settings.timezone}
                          onChange={(e) => updateSetting('timezone', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm"
                        >
                          <option value="Europe/Tirane">Europe/Tiranë</option>
                          <option value="Europe/Rome">Europe/Rome</option>
                          <option value="Europe/London">Europe/London</option>
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Formati i Datës</label>
                        <select
                          value={settings.date_format}
                          onChange={(e) => updateSetting('date_format', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm"
                        >
                          <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                          <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                          <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Formati i Orës</label>
                        <select
                          value={settings.time_format}
                          onChange={(e) => updateSetting('time_format', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm"
                        >
                          <option value="24h">24 Orë</option>
                          <option value="12h">12 Orë</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        <span className="flex items-center gap-1">
                          <Coins className="w-3 h-3" />
                          Monedha Bazë
                        </span>
                      </label>
                      <select
                        value={settings.base_currency}
                        onChange={(e) => updateSetting('base_currency', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 text-sm"
                      >
                        <option value="LEK">LEK - Lek Shqiptar</option>
                        <option value="EUR">EUR - Euro</option>
                        <option value="USD">USD - Dollar</option>
                      </select>
                      <p className="text-xs text-gray-400 mt-1">Shfaqet si monedha kryesore në aplikacion</p>
                    </div>
                  </div>
                </div>

                {/* Logo & Branding */}
                <div className="bg-white border border-gray-200 rounded-xl p-5">
                  <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Image className="w-4 h-4 text-indigo-600" />
                    Logo & Imazhe
                  </h3>
                  <div className="grid grid-cols-3 gap-3">
                    {/* Main Logo */}
                    <div className="border border-dashed border-gray-300 rounded-lg p-3 text-center">
                      <label className="block text-xs font-medium text-gray-600 mb-2">Logo</label>
                      {settings.logo_url ? (
                        <div className="relative group mb-2">
                          <img src={getImageUrl(settings.logo_url)} alt="Logo" className="h-10 mx-auto object-contain" />
                          <button
                            onClick={() => updateSetting('logo_url', '')}
                            className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition flex items-center justify-center"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <div className="w-full h-10 bg-gray-100 rounded flex items-center justify-center mb-2">
                          <Image className="w-5 h-5 text-gray-300" />
                        </div>
                      )}
                      <input
                        type="file"
                        ref={logoInputRef}
                        onChange={(e) => handleFileUpload(e.target.files[0], 'logo')}
                        accept="image/*"
                        className="hidden"
                      />
                      <button
                        onClick={() => logoInputRef.current?.click()}
                        disabled={uploadingLogo}
                        className="text-xs text-blue-600 hover:text-blue-700"
                      >
                        {uploadingLogo ? 'Duke ngarkuar...' : 'Ngarko'}
                      </button>
                    </div>

                    {/* Small Logo */}
                    <div className="border border-dashed border-gray-300 rounded-lg p-3 text-center">
                      <label className="block text-xs font-medium text-gray-600 mb-2">Logo Vogël</label>
                      {settings.small_logo_url ? (
                        <div className="relative group mb-2">
                          <img src={getImageUrl(settings.small_logo_url)} alt="Small Logo" className="h-8 mx-auto object-contain" />
                          <button
                            onClick={() => updateSetting('small_logo_url', '')}
                            className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition flex items-center justify-center"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <div className="w-8 h-8 mx-auto bg-gray-100 rounded flex items-center justify-center mb-2">
                          <Image className="w-4 h-4 text-gray-300" />
                        </div>
                      )}
                      <input
                        type="file"
                        ref={smallLogoInputRef}
                        onChange={(e) => handleFileUpload(e.target.files[0], 'small_logo')}
                        accept="image/*"
                        className="hidden"
                      />
                      <button
                        onClick={() => smallLogoInputRef.current?.click()}
                        disabled={uploadingSmallLogo}
                        className="text-xs text-blue-600 hover:text-blue-700"
                      >
                        {uploadingSmallLogo ? '...' : 'Ngarko'}
                      </button>
                    </div>

                    {/* Favicon */}
                    <div className="border border-dashed border-gray-300 rounded-lg p-3 text-center">
                      <label className="block text-xs font-medium text-gray-600 mb-2">Favicon</label>
                      {settings.favicon_url ? (
                        <div className="relative group mb-2">
                          <img src={getImageUrl(settings.favicon_url)} alt="Favicon" className="h-6 w-6 mx-auto object-contain" />
                          <button
                            onClick={() => updateSetting('favicon_url', '')}
                            className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition flex items-center justify-center"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <div className="w-6 h-6 mx-auto bg-gray-100 rounded flex items-center justify-center mb-2">
                          <Image className="w-3 h-3 text-gray-300" />
                        </div>
                      )}
                      <input
                        type="file"
                        ref={faviconInputRef}
                        onChange={(e) => handleFileUpload(e.target.files[0], 'favicon')}
                        accept="image/*,.ico"
                        className="hidden"
                      />
                      <button
                        onClick={() => faviconInputRef.current?.click()}
                        disabled={uploadingFavicon}
                        className="text-xs text-blue-600 hover:text-blue-700"
                      >
                        {uploadingFavicon ? '...' : 'Ngarko'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'users':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Menaxhimi i Përdoruesve</h3>
                <p className="text-sm text-gray-500">Menaxho të gjithë përdoruesit e sistemit</p>
              </div>
              <div className="flex items-center gap-2">
                <button 
                  onClick={openAddUserModal}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition"
                >
                  <UserPlus className="w-4 h-4" />
                  Shto Përdorues
                </button>
                <button 
                  onClick={fetchAllUsers}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition"
                >
                  <RefreshCw className={`w-4 h-4 ${usersLoading ? 'animate-spin' : ''}`} />
                  Rifresko
                </button>
              </div>
            </div>

            {/* Filters */}
            <div className="flex gap-4 items-center">
              <div className="flex-1 relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Kërko sipas emrit, emailit ose username..."
                  value={userSearchTerm}
                  onChange={(e) => setUserSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                />
              </div>
              <select
                value={userRoleFilter}
                onChange={(e) => setUserRoleFilter(e.target.value)}
                className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
              >
                <option value="all">Të gjithë Rolet</option>
                <option value="admin">Administrator</option>
                <option value="dispatcher">Dispatcher</option>
                <option value="driver">Shofer</option>
              </select>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-gray-900">{allUsers.length}</p>
                <p className="text-xs text-gray-500">Totali</p>
              </div>
              <div className="bg-red-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-red-600">{allUsers.filter(u => u.role === 'admin').length}</p>
                <p className="text-xs text-gray-500">Administratorë</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-blue-600">{allUsers.filter(u => u.role === 'dispatcher').length}</p>
                <p className="text-xs text-gray-500">Dispatcherë</p>
              </div>
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-green-600">{allUsers.filter(u => u.role === 'driver').length}</p>
                <p className="text-xs text-gray-500">Shoferë</p>
              </div>
            </div>

            {/* Users Table */}
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Përdoruesi</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Roli</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase">Statusi</th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Veprime</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {usersLoading ? (
                    <tr>
                      <td colSpan="5" className="px-4 py-8 text-center">
                        <RefreshCw className="w-6 h-6 animate-spin text-gray-400 mx-auto mb-2" />
                        <p className="text-gray-500 text-sm">Duke ngarkuar përdoruesit...</p>
                      </td>
                    </tr>
                  ) : filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-4 py-8 text-center">
                        <Users className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                        <p className="text-gray-500">Nuk u gjet asnjë përdorues</p>
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50 transition">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center text-white font-bold">
                              {user.name?.charAt(0) || user.email?.charAt(0) || '?'}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{user.name || 'Pa emër'}</p>
                              <p className="text-xs text-gray-500">@{user.username || user.email?.split('@')[0]}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-gray-600">{user.email}</p>
                        </td>
                        <td className="px-4 py-3">
                          {getRoleBadge(user.role)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {user.login_enabled === false ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                              <Lock className="w-3 h-3" />
                              I bllokuar
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                              <Unlock className="w-3 h-3" />
                              Aktiv
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => openEditModal(user)}
                              className="p-2 hover:bg-gray-100 rounded-lg transition"
                              title="Ndrysho"
                            >
                              <Edit2 className="w-4 h-4 text-gray-600" />
                            </button>
                            <button
                              onClick={() => toggleUserStatus(user)}
                              className={`p-2 rounded-lg transition ${
                                user.login_enabled === false 
                                  ? 'hover:bg-green-100 text-green-600' 
                                  : 'hover:bg-red-100 text-red-600'
                              }`}
                              title={user.login_enabled === false ? 'Aktivizo Hyrjen' : 'Çaktivizo Hyrjen'}
                            >
                              {user.login_enabled === false ? (
                                <Unlock className="w-4 h-4" />
                              ) : (
                                <Lock className="w-4 h-4" />
                              )}
                            </button>
                            {/* Delete Button - Hidden for admin@tiataxi.al */}
                            {user.email !== 'admin@tiataxi.al' && (
                              <button
                                onClick={() => openDeleteConfirm(user)}
                                className="p-2 hover:bg-red-100 rounded-lg transition text-red-500 hover:text-red-700"
                                title="Fshi Përdoruesin"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && userToDelete && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Konfirmo Fshirjen</h3>
                    <button 
                      onClick={() => {
                        setShowDeleteConfirm(false);
                        setUserToDelete(null);
                      }}
                      className="p-2 hover:bg-gray-100 rounded-full transition"
                    >
                      <X className="w-5 h-5 text-gray-500" />
                    </button>
                  </div>

                  <div className="flex items-center gap-4 mb-6 p-4 bg-red-50 rounded-xl border border-red-200">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center text-white text-xl font-bold">
                      {userToDelete.name?.charAt(0) || '?'}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{userToDelete.name || 'Pa emër'}</p>
                      <p className="text-sm text-gray-500">{userToDelete.email}</p>
                      {getRoleBadge(userToDelete.role)}
                    </div>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                    <p className="text-yellow-800 text-sm">
                      <strong>Kujdes!</strong> Ky veprim është i pakthyeshëm. Të gjitha të dhënat e këtij përdoruesi do të fshihen përgjithmonë.
                    </p>
                  </div>

                  <p className="text-gray-600 mb-6">
                    A jeni i sigurt që dëshironi të fshini përdoruesin <strong>{userToDelete.name}</strong>?
                  </p>

                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setShowDeleteConfirm(false);
                        setUserToDelete(null);
                      }}
                      className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition"
                    >
                      Anulo
                    </button>
                    <button
                      onClick={handleDeleteUser}
                      disabled={deleting}
                      className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:bg-red-400 transition flex items-center justify-center gap-2"
                    >
                      {deleting ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          Duke fshirë...
                        </>
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4" />
                          Fshi Përdoruesin
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Add User Modal */}
            {showAddUserModal && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-gray-900">Shto Përdorues të Ri</h3>
                    <button 
                      onClick={() => setShowAddUserModal(false)}
                      className="p-2 hover:bg-gray-100 rounded-full transition"
                    >
                      <X className="w-5 h-5 text-gray-500" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    {/* Role Selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-2">Roli</label>
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => setAddUserForm({...addUserForm, role: 'dispatcher'})}
                          className={`flex-1 py-2.5 px-4 rounded-lg border-2 font-medium transition ${
                            addUserForm.role === 'dispatcher'
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          Dispatcher
                        </button>
                        <button
                          type="button"
                          onClick={() => setAddUserForm({...addUserForm, role: 'admin'})}
                          className={`flex-1 py-2.5 px-4 rounded-lg border-2 font-medium transition ${
                            addUserForm.role === 'admin'
                              ? 'border-red-500 bg-red-50 text-red-700'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          Administrator
                        </button>
                      </div>
                    </div>

                    {/* Name Fields */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">Emri *</label>
                        <input
                          type="text"
                          value={addUserForm.first_name}
                          onChange={(e) => setAddUserForm({...addUserForm, first_name: e.target.value})}
                          placeholder="Emri"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">Mbiemri</label>
                        <input
                          type="text"
                          value={addUserForm.last_name}
                          onChange={(e) => setAddUserForm({...addUserForm, last_name: e.target.value})}
                          placeholder="Mbiemri"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Email *</label>
                      <input
                        type="email"
                        value={addUserForm.email}
                        onChange={(e) => setAddUserForm({...addUserForm, email: e.target.value})}
                        placeholder="email@example.com"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Username</label>
                      <input
                        type="text"
                        value={addUserForm.username}
                        onChange={(e) => setAddUserForm({...addUserForm, username: e.target.value})}
                        placeholder="Do të gjenerohet automatikisht nga email"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Fjalëkalimi *</label>
                      <div className="relative">
                        <input
                          type={showAddPassword ? 'text' : 'password'}
                          value={addUserForm.password}
                          onChange={(e) => setAddUserForm({...addUserForm, password: e.target.value})}
                          placeholder="Minimum 6 karaktere"
                          className="w-full px-4 py-2.5 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                        />
                        <button
                          type="button"
                          onClick={() => setShowAddPassword(!showAddPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showAddPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Konfirmo Fjalëkalimin *</label>
                      <input
                        type={showAddPassword ? 'text' : 'password'}
                        value={addUserForm.confirmPassword}
                        onChange={(e) => setAddUserForm({...addUserForm, confirmPassword: e.target.value})}
                        placeholder="Shkruaj fjalëkalimin përsëri"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                      />
                    </div>

                    <div className={`rounded-lg p-3 text-sm ${
                      addUserForm.role === 'admin' 
                        ? 'bg-red-50 border border-red-200 text-red-700'
                        : 'bg-blue-50 border border-blue-200 text-blue-700'
                    }`}>
                      <strong>Info:</strong> {addUserForm.role === 'admin' 
                        ? 'Administratori ka akses të plotë në sistem, përfshirë konfigurimet dhe menaxhimin e përdoruesve.'
                        : 'Dispatcher-i ka akses për të krijuar porosi, caktuar shoferë dhe monitoruar udhëtimet.'
                      }
                    </div>
                  </div>

                  <div className="flex gap-3 mt-6">
                    <button
                      onClick={() => setShowAddUserModal(false)}
                      className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition"
                    >
                      Anulo
                    </button>
                    <button
                      onClick={handleAddUser}
                      disabled={addingUser}
                      className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:bg-red-400 transition flex items-center justify-center gap-2"
                    >
                      {addingUser ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          Duke krijuar...
                        </>
                      ) : (
                        <>
                          <UserPlus className="w-4 h-4" />
                          Krijo Përdoruesin
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Edit User Modal */}
            {showEditModal && editingUser && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-gray-900">Ndrysho Përdoruesin</h3>
                    <button 
                      onClick={() => setShowEditModal(false)}
                      className="p-2 hover:bg-gray-100 rounded-full transition"
                    >
                      <X className="w-5 h-5 text-gray-500" />
                    </button>
                  </div>

                  <div className="flex items-center gap-4 mb-6 p-4 bg-gray-50 rounded-xl">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center text-white text-xl font-bold">
                      {editingUser.name?.charAt(0) || '?'}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{editingUser.name || 'Pa emër'}</p>
                      {getRoleBadge(editingUser.role)}
                    </div>
                  </div>

                  <div className="space-y-4">
                    {/* Name Fields */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">Emri</label>
                        <input
                          type="text"
                          value={editForm.first_name}
                          onChange={(e) => setEditForm({...editForm, first_name: e.target.value})}
                          placeholder="Emri"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">Mbiemri</label>
                        <input
                          type="text"
                          value={editForm.last_name}
                          onChange={(e) => setEditForm({...editForm, last_name: e.target.value})}
                          placeholder="Mbiemri"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Username</label>
                      <input
                        type="text"
                        value={editForm.username}
                        onChange={(e) => setEditForm({...editForm, username: e.target.value})}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Email</label>
                      <input
                        type="email"
                        value={editForm.email}
                        onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                      />
                    </div>

                    <div className="border-t border-gray-200 pt-4 mt-4">
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        Fjalëkalimi i Ri (opsional)
                      </label>
                      <div className="relative">
                        <input
                          type={showNewPassword ? 'text' : 'password'}
                          value={editForm.newPassword}
                          onChange={(e) => setEditForm({...editForm, newPassword: e.target.value})}
                          placeholder="Lëre bosh nëse nuk dëshiron të ndryshosh"
                          className="w-full px-4 py-2.5 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    {editForm.newPassword && (
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">
                          Konfirmo Fjalëkalimin e Ri
                        </label>
                        <input
                          type={showNewPassword ? 'text' : 'password'}
                          value={editForm.confirmPassword}
                          onChange={(e) => setEditForm({...editForm, confirmPassword: e.target.value})}
                          placeholder="Shkruaj fjalëkalimin përsëri"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                        />
                      </div>
                    )}

                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-700">
                      <strong>Kujdes:</strong> Nëse ndryshon fjalëkalimin, përdoruesi do të duhet të identifikohet përsëri me fjalëkalimin e ri.
                    </div>
                  </div>

                  <div className="flex gap-3 mt-6">
                    <button
                      onClick={() => setShowEditModal(false)}
                      className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition"
                    >
                      Anulo
                    </button>
                    <button
                      onClick={handleUpdateUser}
                      disabled={saving}
                      className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:bg-red-400 transition flex items-center justify-center gap-2"
                    >
                      {saving ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          Duke ruajtur...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          Ruaj Ndryshimet
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 'exchange':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Kursi i Këmbimit EUR/LEK</h3>
                <p className="text-sm text-gray-500">Vendos kursin e këmbimit për konvertimin e monedhave</p>
              </div>
            </div>

            {/* Current Rate + Update Form - Inline Layout */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-xl p-6 text-white">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                {/* Left: Current Rate */}
                <div className="flex items-center gap-4">
                  <div>
                    <p className="text-blue-100 text-sm mb-1">Kursi Aktual</p>
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl font-bold">1 EUR</span>
                      <span className="text-xl">=</span>
                      <span className="text-3xl font-bold">{exchangeRate.eur_to_lek || 100} LEK</span>
                    </div>
                    {exchangeRate.updated_at && (
                      <p className="text-xs text-blue-200 mt-2">
                        Përditësuar: {new Date(exchangeRate.updated_at).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        {exchangeRate.updated_by_name && ` nga ${exchangeRate.updated_by_name}`}
                      </p>
                    )}
                  </div>
                </div>

                {/* Right: Update Form - Inline */}
                <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-blue-100 whitespace-nowrap">Kursi i Ri:</span>
                    <input
                      type="number"
                      step="0.01"
                      value={newRate}
                      onChange={(e) => setNewRate(e.target.value)}
                      placeholder={String(exchangeRate.eur_to_lek || 100)}
                      className="w-24 px-3 py-2 border-0 bg-white/20 text-white placeholder-blue-200 rounded-lg focus:ring-2 focus:ring-white/50 text-center font-semibold"
                    />
                    <span className="text-sm text-blue-100">LEK</span>
                  </div>
                  <button
                    onClick={updateExchangeRate}
                    disabled={saving || !newRate}
                    className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-blue-50 disabled:bg-gray-300 text-blue-700 rounded-lg font-medium transition text-sm"
                  >
                    {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Ruaj
                  </button>
                </div>
              </div>
            </div>

            {/* Info Note */}
            <p className="text-xs text-gray-400 -mt-4 ml-1">
              Kursi i ri do të përdoret për të gjitha faturat dhe llogaritjet e reja. Faturat ekzistuese nuk do të ndryshohen.
            </p>

            {/* History Log */}
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-md font-semibold text-gray-900">Historiku i Ndryshimeve</h4>
                <button
                  onClick={fetchExchangeHistory}
                  className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                >
                  <RefreshCw className="w-3 h-3" />
                  Rifresko
                </button>
              </div>
              
              {exchangeHistory.length === 0 ? (
                <p className="text-gray-400 text-sm text-center py-4">Nuk ka ndryshime të regjistruara</p>
              ) : (
                <div className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
                  {exchangeHistory.map((log, idx) => (
                    <div key={log.id || idx} className="py-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                          <Globe className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-800">
                            {log.old_rate} → {log.new_rate} LEK/EUR
                          </p>
                          <p className="text-xs text-gray-500">
                            {log.changed_by_name}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">
                          {new Date(log.changed_at).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                        </p>
                        <p className="text-xs text-gray-400">
                          {new Date(log.changed_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );

      case 'branding':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Detajet e Markës</h3>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Emri i Kompanisë</label>
              <input
                type="text"
                value={settings.company_name}
                onChange={(e) => updateSetting('company_name', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Slogani</label>
              <input
                type="text"
                value={settings.tagline}
                onChange={(e) => updateSetting('tagline', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Logo URL</label>
                <input
                  type="text"
                  value={settings.logo_url || ''}
                  onChange={(e) => updateSetting('logo_url', e.target.value)}
                  placeholder="https://..."
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Favicon URL</label>
                <input
                  type="text"
                  value={settings.favicon_url || ''}
                  onChange={(e) => updateSetting('favicon_url', e.target.value)}
                  placeholder="https://..."
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Email Mbështetje</label>
                <input
                  type="email"
                  value={settings.support_email}
                  onChange={(e) => updateSetting('support_email', e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Tel. Mbështetje</label>
                <input
                  type="tel"
                  value={settings.support_phone}
                  onChange={(e) => updateSetting('support_phone', e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg"
                />
              </div>
            </div>
          </div>
        );

      case 'dispatch':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Cilësimet e Dispatch</h3>
            <div className="space-y-4">
              <ToggleSetting
                label="Caktimi Automatik"
                description="Cakto automatikisht shoferin më të afërt"
                checked={settings.auto_assign}
                onChange={(val) => updateSetting('auto_assign', val)}
              />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Koha Max. Pritje (min)</label>
                  <input
                    type="number"
                    value={settings.max_wait_time}
                    onChange={(e) => updateSetting('max_wait_time', parseInt(e.target.value))}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Rrezja Kërkimit (km)</label>
                  <input
                    type="number"
                    value={settings.search_radius}
                    onChange={(e) => updateSetting('search_radius', parseFloat(e.target.value))}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
              <ToggleSetting
                label="Prioritizo sipas Vlerësimit"
                description="Shoferët me vlerësim më të lartë kanë prioritet"
                checked={settings.prioritize_rating}
                onChange={(val) => updateSetting('prioritize_rating', val)}
              />
              <ToggleSetting
                label="Njoftime SMS për Shoferët"
                description="Dërgo SMS kur ka porosi të re"
                checked={settings.notify_driver_sms}
                onChange={(val) => updateSetting('notify_driver_sms', val)}
              />
            </div>
          </div>
        );

      case 'taximeter':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Taksimetri Dixhital</h3>
                <p className="text-sm text-gray-500">Konfiguro tarifat që shfaqen në aplikacionin e shoferëve</p>
              </div>
              <div className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-medium">
                Konfigurime
              </div>
            </div>
            
            {/* Preview Card */}
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-6 text-white">
              <div className="flex items-center gap-2 mb-4">
                <Settings className="w-5 h-5 text-yellow-400" />
                <span className="text-sm text-gray-400">Paraqitje e Tarifave</span>
              </div>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-3xl font-bold text-yellow-400">{taximeterSettings.base_fare}</p>
                  <p className="text-xs text-gray-400">Tarifa Bazë (L)</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-green-400">{taximeterSettings.per_km}</p>
                  <p className="text-xs text-gray-400">Për KM (L)</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-blue-400">{taximeterSettings.per_minute}</p>
                  <p className="text-xs text-gray-400">Për Minutë (L)</p>
                </div>
              </div>
            </div>

            {/* Basic Rates */}
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                Tarifat Bazë
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Tarifa Fillestare (LEK)</label>
                  <input
                    type="number"
                    value={taximeterSettings.base_fare}
                    onChange={(e) => updateTaximeterSetting('base_fare', parseInt(e.target.value) || 0)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">Çmimi fillestar për çdo udhëtim</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Çmimi për KM (LEK)</label>
                  <input
                    type="number"
                    value={taximeterSettings.per_km}
                    onChange={(e) => updateTaximeterSetting('per_km', parseInt(e.target.value) || 0)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">Çmimi për çdo kilometër</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Çmimi për Minutë (LEK)</label>
                  <input
                    type="number"
                    value={taximeterSettings.per_minute}
                    onChange={(e) => updateTaximeterSetting('per_minute', parseInt(e.target.value) || 0)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">Çmimi pas 2 minutave të para</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Tarifa Minimale (LEK)</label>
                  <input
                    type="number"
                    value={taximeterSettings.min_fare}
                    onChange={(e) => updateTaximeterSetting('min_fare', parseInt(e.target.value) || 0)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">Çmimi minimal për udhëtim</p>
                </div>
              </div>
            </div>

            {/* Waiting Rate */}
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                Tarifa e Pritjes
              </h4>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Çmimi për Minutë Pritje (LEK)</label>
                <input
                  type="number"
                  value={taximeterSettings.waiting_per_minute}
                  onChange={(e) => updateTaximeterSetting('waiting_per_minute', parseInt(e.target.value) || 0)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                />
                <p className="text-xs text-gray-400 mt-1">Aplikohet kur shoferi pret pasagjerin</p>
              </div>
            </div>

            {/* Surcharges */}
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                Shtesa (Surcharges)
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Shtesa Natës (%)</label>
                  <input
                    type="number"
                    step="1"
                    value={Math.round(taximeterSettings.night_surcharge * 100)}
                    onChange={(e) => updateTaximeterSetting('night_surcharge', (parseInt(e.target.value) || 0) / 100)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">Përqindja shtesë gjatë natës</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Shtesa Fundjavës (%)</label>
                  <input
                    type="number"
                    step="1"
                    value={Math.round(taximeterSettings.weekend_surcharge * 100)}
                    onChange={(e) => updateTaximeterSetting('weekend_surcharge', (parseInt(e.target.value) || 0) / 100)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">Përqindja shtesë në fundjavë</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Ora Fillimit të Natës</label>
                  <select
                    value={taximeterSettings.night_start_hour}
                    onChange={(e) => updateTaximeterSetting('night_start_hour', parseInt(e.target.value))}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                  >
                    {Array.from({length: 24}, (_, i) => (
                      <option key={i} value={i}>{i.toString().padStart(2, '0')}:00</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Ora Mbarimit të Natës</label>
                  <select
                    value={taximeterSettings.night_end_hour}
                    onChange={(e) => updateTaximeterSetting('night_end_hour', parseInt(e.target.value))}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                  >
                    {Array.from({length: 24}, (_, i) => (
                      <option key={i} value={i}>{i.toString().padStart(2, '0')}:00</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Currency Settings */}
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                Monedha
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Kursi EUR → LEK</label>
                  <input
                    type="number"
                    value={taximeterSettings.eur_rate}
                    onChange={(e) => updateTaximeterSetting('eur_rate', parseInt(e.target.value) || 100)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">1 EUR = {taximeterSettings.eur_rate} LEK</p>
                </div>
              </div>
            </div>

            {/* Example Calculation */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
              <h4 className="font-semibold text-blue-900 mb-3">Shembull Llogaritjeje</h4>
              <p className="text-sm text-blue-700 mb-2">Udhëtim 10 KM, 15 minuta, pa pritje, ditë normale:</p>
              <div className="bg-white rounded-lg p-3 text-sm">
                <div className="flex justify-between py-1 border-b">
                  <span className="text-gray-600">Tarifa Bazë</span>
                  <span className="font-medium">{taximeterSettings.base_fare} L</span>
                </div>
                <div className="flex justify-between py-1 border-b">
                  <span className="text-gray-600">Distanca (10 km × {taximeterSettings.per_km} L)</span>
                  <span className="font-medium">{10 * taximeterSettings.per_km} L</span>
                </div>
                <div className="flex justify-between py-1 border-b">
                  <span className="text-gray-600">Koha (13 min × {taximeterSettings.per_minute} L)</span>
                  <span className="font-medium">{13 * taximeterSettings.per_minute} L</span>
                </div>
                <div className="flex justify-between py-2 font-bold text-blue-900">
                  <span>TOTALI</span>
                  <span>{taximeterSettings.base_fare + (10 * taximeterSettings.per_km) + (13 * taximeterSettings.per_minute)} L</span>
                </div>
              </div>
            </div>
          </div>
        );

      case 'notifications':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Cilësimet e Njoftimeve</h3>
            <div className="space-y-4">
              <h4 className="font-medium text-gray-700">Kanalet e Njoftimeve</h4>
              <ToggleSetting
                label="Njoftimet me Email"
                description="Merr njoftime nëpërmjet emailit"
                checked={settings.email_notifications}
                onChange={(val) => updateSetting('email_notifications', val)}
              />
              <ToggleSetting
                label="Njoftimet SMS"
                description="Merr njoftime nëpërmjet SMS"
                checked={settings.sms_notifications}
                onChange={(val) => updateSetting('sms_notifications', val)}
              />
              <ToggleSetting
                label="Push Notifications"
                description="Njoftime në browser ose aplikacion"
                checked={settings.push_notifications}
                onChange={(val) => updateSetting('push_notifications', val)}
              />
              
              <h4 className="font-medium text-gray-700 mt-6">Lloji i Njoftimeve</h4>
              <ToggleSetting
                label="Porosi e Re"
                description="Njofto kur ka porosi të re"
                checked={settings.new_ride_alert}
                onChange={(val) => updateSetting('new_ride_alert', val)}
              />
              <ToggleSetting
                label="Shofer Offline"
                description="Njofto kur një shofer bëhet offline"
                checked={settings.driver_offline_alert}
                onChange={(val) => updateSetting('driver_offline_alert', val)}
              />
              <ToggleSetting
                label="Raport Ditor"
                description="Dërgo raport të përditshëm çdo ditë"
                checked={settings.daily_report}
                onChange={(val) => updateSetting('daily_report', val)}
              />
            </div>
          </div>
        );

      case 'expense_categories':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Kategoritë e Shpenzimeve</h3>
                <p className="text-sm text-gray-500">Menaxho kategoritë për shpenzimet e automjeteve</p>
              </div>
              <button
                onClick={async () => {
                  const name = window.prompt('Emri i kategorisë së re:');
                  if (name) {
                    try {
                      const newCategory = { id: name.toLowerCase().replace(/\s+/g, '_'), name, active: true };
                      await api.post('/settings/expense-categories', newCategory);
                      toast.success('Kategoria u shtua me sukses');
                      fetchSettings();
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Gabim gjatë shtimit të kategorisë');
                    }
                  }
                }}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                <Plus size={18} />
                Shto Kategori
              </button>
            </div>
            
            <div className="bg-white rounded-xl border overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Kategoria</th>
                    <th className="text-center px-4 py-3 text-sm font-semibold text-gray-600">Aktive</th>
                    <th className="text-center px-4 py-3 text-sm font-semibold text-gray-600">Veprime</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {(settings.expense_categories || getDefaultExpenseCategories()).map((cat, idx) => (
                    <tr key={cat.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
                            <Banknote size={16} className="text-yellow-600" />
                          </div>
                          <span className="font-medium text-gray-900">{cat.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                          cat.active !== false ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                        }`}>
                          {cat.active !== false ? 'Po' : 'Jo'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={async () => {
                              const categories = settings.expense_categories || getDefaultExpenseCategories();
                              categories[idx].active = !categories[idx].active;
                              try {
                                await api.put('/settings/expense-categories', { categories });
                                toast.success('Statusi u ndryshua');
                                fetchSettings();
                              } catch (err) {
                                toast.error('Gabim gjatë ndryshimit të statusit');
                              }
                            }}
                            className={`p-1.5 rounded transition ${
                              cat.active !== false 
                                ? 'text-yellow-600 hover:bg-yellow-50' 
                                : 'text-green-600 hover:bg-green-50'
                            }`}
                            title={cat.active !== false ? 'Çaktivizo' : 'Aktivizo'}
                          >
                            {cat.active !== false ? <X size={14} /> : <Check size={14} />}
                          </button>
                          <button
                            onClick={async () => {
                              if (window.confirm(`Jeni i sigurt që doni të fshini "${cat.name}"?`)) {
                                try {
                                  await api.delete(`/settings/expense-categories/${cat.id}`);
                                  toast.success('Kategoria u fshi me sukses');
                                  fetchSettings();
                                } catch (err) {
                                  toast.error(err.response?.data?.detail || 'Gabim gjatë fshirjes');
                                }
                              }
                            }}
                            className="p-1.5 text-red-500 hover:bg-red-50 rounded transition"
                            title="Fshi"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-700">
                <strong>Info:</strong> Këto kategori përdoren kur regjistrohen shpenzime për automjetet. 
                Kategoritë e çaktivizuara nuk do të shfaqen në formularin e shpenzimeve.
                Shpenzimet e automjeteve lidhen automatikisht me Bilancin Ekonomik.
              </p>
            </div>
          </div>
        );

      case 'payment_methods':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Metodat e Pagesës</h3>
                <p className="text-sm text-gray-500">Menaxho metodat e pagesës për faturat dhe udhëtimet</p>
              </div>
            </div>
            
            <div className="bg-white rounded-xl border overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Metoda</th>
                    <th className="text-center px-4 py-3 text-sm font-semibold text-gray-600">Për Dispatcher</th>
                    <th className="text-center px-4 py-3 text-sm font-semibold text-gray-600">Për Shofer</th>
                    <th className="text-center px-4 py-3 text-sm font-semibold text-gray-600">Aktive</th>
                    <th className="text-center px-4 py-3 text-sm font-semibold text-gray-600">Veprime</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {(settings.payment_methods || getDefaultPaymentMethods()).map((method, idx) => (
                    <tr key={method.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                            method.id === 'cash' ? 'bg-green-100' : 'bg-blue-100'
                          }`}>
                            {method.id === 'cash' ? (
                              <Banknote size={16} className="text-green-600" />
                            ) : (
                              <CreditCard size={16} className="text-blue-600" />
                            )}
                          </div>
                          <span className="font-medium text-gray-900">{method.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={method.for_dispatcher !== false}
                          onChange={async () => {
                            const methods = settings.payment_methods || getDefaultPaymentMethods();
                            methods[idx].for_dispatcher = !methods[idx].for_dispatcher;
                            try {
                              await api.put('/settings/payment-methods', { payment_methods: methods });
                              toast.success('Ndryshimi u ruajt');
                              fetchSettings();
                            } catch (err) {
                              toast.error('Gabim gjatë ruajtjes');
                            }
                          }}
                          className="rounded"
                        />
                      </td>
                      <td className="px-4 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={method.for_driver !== false}
                          onChange={async () => {
                            const methods = settings.payment_methods || getDefaultPaymentMethods();
                            methods[idx].for_driver = !methods[idx].for_driver;
                            try {
                              await api.put('/settings/payment-methods', { payment_methods: methods });
                              toast.success('Ndryshimi u ruajt');
                              fetchSettings();
                            } catch (err) {
                              toast.error('Gabim gjatë ruajtjes');
                            }
                          }}
                          className="rounded"
                          disabled={method.id === 'cash'}
                        />
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                          method.active !== false ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                        }`}>
                          {method.active !== false ? 'Po' : 'Jo'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={async () => {
                              const methods = settings.payment_methods || getDefaultPaymentMethods();
                              methods[idx].active = !methods[idx].active;
                              try {
                                await api.put('/settings/payment-methods', { payment_methods: methods });
                                toast.success('Statusi u ndryshua');
                                fetchSettings();
                              } catch (err) {
                                toast.error('Gabim gjatë ndryshimit të statusit');
                              }
                            }}
                            className={`p-1.5 rounded transition ${
                              method.active !== false 
                                ? 'text-yellow-600 hover:bg-yellow-50' 
                                : 'text-green-600 hover:bg-green-50'
                            }`}
                            title={method.active !== false ? 'Çaktivizo' : 'Aktivizo'}
                          >
                            {method.active !== false ? <X size={14} /> : <Check size={14} />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-700">
                <strong>Info:</strong> Metodat e pagesës kontrollohen sipas roleve:
                <br/>• <strong>Cash</strong> - Disponohet për dispatcher dhe shofer (obligative për shofer)
                <br/>• <strong>POS Bankar</strong> - Disponohet vetëm për dispatcher kur krijon faturë
              </p>
            </div>
          </div>
        );

      case 'smtp_config':
        return <EmailReportsSection />;

      case 'api':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Integrime API</h3>
                <p className="text-sm text-gray-500">Konfiguro çelësat API për shërbimet e jashtme</p>
              </div>
            </div>

            {/* Google Maps API */}
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center flex-shrink-0">
                  <Map className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h4 className="text-base font-semibold text-gray-900 mb-1">Google Maps Platform</h4>
                  <p className="text-sm text-gray-500 mb-4">
                    Çelësi API përdoret për hartat, gjeolokalizimin dhe autocomplete në të gjitha aplikacionet:
                  </p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">Admin Panel</span>
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">Dispatcher Panel</span>
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">Driver App</span>
                    <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full">Passenger App</span>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Google Maps API Key</label>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <input
                            type={settings.show_api_key ? 'text' : 'password'}
                            value={settings.google_maps_api_key || ''}
                            onChange={(e) => updateSetting('google_maps_api_key', e.target.value)}
                            placeholder="AIzaSy..."
                            className="w-full px-4 py-2.5 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 font-mono text-sm"
                          />
                          <button
                            type="button"
                            onClick={() => updateSetting('show_api_key', !settings.show_api_key)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            {settings.show_api_key ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        <button
                          onClick={() => {
                            if (settings.google_maps_api_key) {
                              toast.success('API Key u ruajt me sukses!');
                            }
                          }}
                          className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition flex items-center gap-2"
                        >
                          <Check className="w-4 h-4" />
                          Testo
                        </button>
                      </div>
                      <p className="text-xs text-gray-400 mt-2">
                        Merr çelësin nga{' '}
                        <a 
                          href="https://console.cloud.google.com/google/maps-apis" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-green-600 hover:underline"
                        >
                          Google Cloud Console
                        </a>
                      </p>
                    </div>
                  </div>

                  {/* API Services Info */}
                  <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs font-medium text-gray-600 mb-2">Shërbimet që përdorin këtë çelës:</p>
                    <div className="grid grid-cols-2 gap-2 text-xs text-gray-500">
                      <div className="flex items-center gap-1">
                        <Check className="w-3 h-3 text-green-500" />
                        Maps JavaScript API
                      </div>
                      <div className="flex items-center gap-1">
                        <Check className="w-3 h-3 text-green-500" />
                        Places API
                      </div>
                      <div className="flex items-center gap-1">
                        <Check className="w-3 h-3 text-green-500" />
                        Geocoding API
                      </div>
                      <div className="flex items-center gap-1">
                        <Check className="w-3 h-3 text-green-500" />
                        Directions API
                      </div>
                      <div className="flex items-center gap-1">
                        <Check className="w-3 h-3 text-green-500" />
                        Distance Matrix API
                      </div>
                      <div className="flex items-center gap-1">
                        <Check className="w-3 h-3 text-green-500" />
                        Maps SDK (Android/iOS)
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <Bell className="w-4 h-4 text-amber-600" />
                </div>
                <div>
                  <h5 className="text-sm font-medium text-amber-800">Kujdes me Sigurinë</h5>
                  <p className="text-xs text-amber-700 mt-1">
                    Çelësi API duhet të ketë kufizimet e duhura në Google Cloud Console. 
                    Rekomandohet të kufizoni përdorimin sipas domain-it dhe IP-së për të parandaluar keqpërdorimin.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'password':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Ndrysho Fjalëkalimin</h3>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Fjalëkalimi Aktual</label>
              <input
                type="password"
                value={passwordSettings.currentPassword}
                onChange={(e) => setPasswordSettings({...passwordSettings, currentPassword: e.target.value})}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg"
                placeholder="••••••••"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Fjalëkalimi i Ri</label>
              <input
                type="password"
                value={passwordSettings.newPassword}
                onChange={(e) => setPasswordSettings({...passwordSettings, newPassword: e.target.value})}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg"
                placeholder="••••••••"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Konfirmo Fjalëkalimin</label>
              <input
                type="password"
                value={passwordSettings.confirmPassword}
                onChange={(e) => setPasswordSettings({...passwordSettings, confirmPassword: e.target.value})}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg"
                placeholder="••••••••"
              />
            </div>
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-blue-700 font-medium mb-2">Kërkesat e fjalëkalimit:</p>
              <ul className="text-sm text-blue-600 space-y-1">
                <li>• Minimumi 8 karaktere</li>
                <li>• Të paktën një shkronjë e madhe</li>
                <li>• Të paktën një numër</li>
                <li>• Të paktën një karakter special</li>
              </ul>
            </div>
          </div>
        );

      case 'license':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Licenca e Softuerit</h3>
            <div className="bg-green-50 border border-green-200 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                  <Check className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-green-800">Licenca Aktive</p>
                  <p className="text-sm text-green-600">TIATaxi Pro - Enterprise Edition</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="bg-white p-3 rounded-lg">
                  <p className="text-xs text-gray-500">Çelësi i Licencës</p>
                  <p className="font-mono text-sm">TAXI-XXXX-XXXX-XXXX</p>
                </div>
                <div className="bg-white p-3 rounded-lg">
                  <p className="text-xs text-gray-500">Skadon më</p>
                  <p className="font-medium">31 Dhjetor 2026</p>
                </div>
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h4 className="font-medium text-gray-900 mb-4">Karakteristikat e Përfshira</h4>
              <div className="grid grid-cols-2 gap-3">
                {[
                  'Shoferë të Pakufizuar', 'Automjete të Pakufizuara', 'Raporte të Avancuara',
                  'API Access', 'Mbështetje 24/7', 'Përditësime Automatike',
                  'Multi-language', 'White-label', 'Custom Integrations'
                ].map(feature => (
                  <div key={feature} className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span className="text-sm text-gray-600">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="p-6">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900">Konfigurime</h1>
        <p className="text-sm text-gray-500 mt-1">Konfiguro cilësimet e llogarisë dhe preferencat</p>
      </div>

      <div className="flex gap-6">
        {/* Sidebar Menu */}
        <div className="w-64 flex-shrink-0">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {menuItems.map(item => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition ${
                  activeTab === item.id 
                    ? 'bg-red-500 text-white' 
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <item.icon size={18} />
                <span className="font-medium text-sm">{item.label}</span>
                {activeTab === item.id && <ChevronRight size={16} className="ml-auto" />}
              </button>
            ))}
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            {renderContent()}
            
            {/* Save Button */}
            {activeTab !== 'license' && (
              <div className="mt-8 pt-6 border-t border-gray-200 flex justify-end">
                <button
                  onClick={activeTab === 'password' ? handlePasswordChange : activeTab === 'taximeter' ? saveTaximeterSettings : handleSave}
                  disabled={saving}
                  className="flex items-center gap-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white px-6 py-2.5 rounded-lg font-semibold transition"
                >
                  {saving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Duke ruajtur...
                    </>
                  ) : (
                    <>
                      <Save size={18} />
                      Ruaj Ndryshimet
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Toggle Setting Component
const ToggleSetting = ({ label, description, checked, onChange }) => (
  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
    <div>
      <p className="font-medium text-gray-900">{label}</p>
      <p className="text-sm text-gray-500">{description}</p>
    </div>
    <ToggleSwitch checked={checked} onChange={onChange} />
  </div>
);

// Toggle Switch Component
const ToggleSwitch = ({ checked, onChange, color = 'red' }) => {
  const colors = {
    red: 'peer-checked:bg-red-500 peer-focus:ring-red-300',
    yellow: 'peer-checked:bg-yellow-500 peer-focus:ring-yellow-300',
    green: 'peer-checked:bg-green-500 peer-focus:ring-green-300'
  };
  
  return (
    <label className="relative inline-flex items-center cursor-pointer">
      <input 
        type="checkbox" 
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only peer" 
      />
      <div className={`w-11 h-6 bg-gray-200 peer-focus:ring-4 ${colors[color]} rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all`}></div>
    </label>
  );
};

export default SettingsPage;
